/**
 * Formats analytics metric data into markdown tables.
 *
 * Each group of rows (defined by a groupBy field) becomes a section with a
 * heading and a table where each row is a variation.
 */

/** Metrics that should be formatted as percentages. */
const PCT_METRICS = new Set([
  "conversion_rate",
  "add_to_cart_rate",
  "checkout_begin_rate",
  "checkout_enter_contact_info_rate",
  "checkout_address_submitted_rate",
  "abandoned_cart_rate",
  "abandoned_checkout_rate",
  "pct_orders_free_shipping",
  "pct_subscription_orders",
  "view_collection_page_rate",
  "view_product_page_rate",
]);

export interface MetricValue {
  value?: number | null;
  ci_low?: number | null;
  ci_high?: number | null;
  p2bb?: number | null;
  p2bc?: number | null;
  uplift?: { value?: number | null } | null;
}

export function formatMetricValue(
  data: MetricValue | number | null | undefined,
  isPct: boolean
): string {
  if (data == null) {
    return "Null";
  }
  if (typeof data === "number") {
    return formatNumber(data, isPct);
  }
  if (data.value == null) {
    return "Null";
  }

  let out = formatNumber(data.value, isPct);

  if (data.ci_low != null && data.ci_high != null) {
    const lo = formatNumber(data.ci_low, isPct);
    const hi = formatNumber(data.ci_high, isPct);
    out += ` (95% CI: ${lo} to ${hi})`;
  }
  if (data.p2bb != null) {
    out += ` (p2bb: ${formatPct(data.p2bb)})`;
  }
  if (data.p2bc != null) {
    out += ` (p2bc: ${formatPct(data.p2bc)})`;
  }
  if (data.uplift?.value != null) {
    const sign = data.uplift.value > 0 ? "+" : "";
    out += ` (${sign}${formatPct(data.uplift.value)} uplift)`;
  }
  return out;
}

function formatNumber(value: number, isPct: boolean): string {
  if (isPct) {
    return `${(value * 100).toFixed(2)}%`;
  }
  return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(4);
}

function formatPct(value: number): string {
  return `${Math.round(value * 100)}%`;
}

export function isPercentMetric(metricName: string): boolean {
  return PCT_METRICS.has(metricName);
}

/**
 * Fields that are always treated as index (header/column identity) rather
 * than metric values. `variation_name` and `is_control` are not present in the
 * raw metric rows; they are joined in from `FormatTableOptions.variations`.
 */
const VARIATION_FIELDS = new Set([
  "variation_id",
  "variation_name",
  "is_control",
]);

const KNOWN_INDEX_FIELDS = new Set([
  ...VARIATION_FIELDS,
  "dt",
  "audience",
  "product_title",
  "product_id",
  "variant_title",
  "variant_id",
]);

/**
 * Variation metadata used to enrich metric rows. The raw metric rows are keyed
 * only by `variation_id`; the human-readable name, control flag, and ordering
 * live on the variation entity and must be supplied by the caller.
 */
export interface VariationInfo {
  id: string;
  name: string;
  isControl: boolean;
  order: number;
}

export interface FormatTableOptions {
  /**
   * Field(s) to group rows by. Each distinct value of this field gets its own
   * heading + table. Pass a string or array of strings.
   * If omitted, all rows go into a single flat table.
   */
  groupBy?: string | string[];
  /**
   * When provided, rows are joined to these variations on `variation_id`:
   * `variation_name` and `is_control` columns are added, rows are sorted by
   * variation `order`, and rows with no matching variation are dropped.
   * When omitted, rows are formatted as-is (no extra columns, no reordering).
   */
  variations?: VariationInfo[];
}

/**
 * Format an array of metric row objects into grouped markdown tables.
 *
 * Output example (groupBy: "dt"):
 * ```
 * # dt: 2025-01-01
 * | variation_id | variation_name | cvr | n_orders |
 * | --- | --- | --- | --- |
 * | abcd-1234 | Control | 5.00% | 100 |
 * | efgh-5678 | Test A | 6.00% (p2bc: 85%) | 120 |
 * ```
 */
export function formatMetricsAsTable(
  rows: Record<string, unknown>[],
  options?: FormatTableOptions
): string {
  if (rows.length === 0) {
    return "No data available.";
  }

  const rowsToFormat = options?.variations?.length
    ? applyVariationInfo(rows, options.variations)
    : rows;

  if (rowsToFormat.length === 0) {
    return "No data available.";
  }

  const groupByFields = normalizeGroupBy(options?.groupBy);
  const allGroupFields = new Set([...KNOWN_INDEX_FIELDS, ...groupByFields]);

  // Determine metric columns from the first row
  const metricColumns = Object.keys(rowsToFormat[0]).filter(
    (k) => !allGroupFields.has(k)
  );

  // Determine variation columns present in the data
  const variationColumns = Object.keys(rowsToFormat[0]).filter((k) =>
    VARIATION_FIELDS.has(k)
  );

  if (groupByFields.length === 0) {
    return buildTable(rowsToFormat, variationColumns, metricColumns);
  }

  // Timeseries: pivot so each metric gets its own table with
  // dt | <variation columns> | value
  if (groupByFields.length === 1 && groupByFields[0] === "dt") {
    return buildTimeseriesTables(
      rowsToFormat,
      groupByFields[0],
      variationColumns,
      metricColumns
    );
  }

  // Group rows by the groupBy field values
  const groups = groupRows(rowsToFormat, groupByFields);
  const parts: string[] = [];

  for (const [groupKey, groupedRows] of groups) {
    parts.push(
      `# ${groupKey}\n${buildTable(groupedRows, variationColumns, metricColumns)}`
    );
  }

  return parts.join("\n\n");
}

function normalizeGroupBy(groupBy?: string | string[]): string[] {
  if (!groupBy) {
    return [];
  }
  return Array.isArray(groupBy) ? groupBy : [groupBy];
}

/**
 * Join metric rows to their variations on `variation_id`: add `variation_name`
 * and `is_control`, sort ascending by variation `order`, and drop rows whose
 * `variation_id` has no matching variation.
 */
function applyVariationInfo(
  rows: Record<string, unknown>[],
  variations: VariationInfo[]
): Record<string, unknown>[] {
  const byId = new Map(variations.map((v) => [v.id, v]));

  return rows
    .flatMap((row) => {
      const info = byId.get(String(row["variation_id"]));
      if (!info) {
        return [];
      }
      return [
        {
          order: info.order,
          row: {
            ...row,
            variation_name: info.name,
            is_control: info.isControl,
          },
        },
      ];
    })
    .sort((a, b) => a.order - b.order)
    .map((entry) => entry.row);
}

function groupRows(
  rows: Record<string, unknown>[],
  groupByFields: string[]
): Map<string, Record<string, unknown>[]> {
  const groups = new Map<string, Record<string, unknown>[]>();

  for (const row of rows) {
    const keyParts = groupByFields.map((f) => {
      const val = row[f];
      const display =
        typeof val === "string" || typeof val === "number"
          ? val
          : JSON.stringify(val);
      return `${f}: ${display}`;
    });
    const key = keyParts.join(" | ");

    let group = groups.get(key);
    if (!group) {
      group = [];
      groups.set(key, group);
    }
    group.push(row);
  }

  return groups;
}

/**
 * Pivot timeseries rows into one table per metric. Each table is keyed by
 * `dt` plus whichever variation columns are present (`variation_id`, and —
 * when the caller supplied `variations` — `variation_name` and `is_control`),
 * followed by the metric's value. When no variation metadata is joined in,
 * this degrades to the original `dt | variation_id | value` shape.
 */
function buildTimeseriesTables(
  rows: Record<string, unknown>[],
  dtField: string,
  variationColumns: string[],
  metricColumns: string[]
): string {
  const parts: string[] = [];
  const columns = [dtField, ...variationColumns, "value"];
  const separator = `| ${columns.map(() => "---").join(" | ")} |`;

  for (const metric of metricColumns) {
    const isPct = isPercentMetric(metric);
    const header = `| ${columns.join(" | ")} |`;
    const dataRows = rows.map((row) => {
      const cells = columns.map((col) => {
        if (col === "value") {
          return formatMetricValue(row[metric] as MetricValue, isPct);
        }
        const val = row[col];
        return typeof val === "string" ||
          typeof val === "number" ||
          typeof val === "boolean"
          ? String(val)
          : JSON.stringify(val);
      });
      return `| ${cells.join(" | ")} |`;
    });
    parts.push(`# ${metric}\n${[header, separator, ...dataRows].join("\n")}`);
  }

  return parts.join("\n\n");
}

function buildTable(
  rows: Record<string, unknown>[],
  variationColumns: string[],
  metricColumns: string[]
): string {
  const columns = [...variationColumns, ...metricColumns];
  const headerRow = `| ${columns.join(" | ")} |`;
  const separatorRow = `| ${columns.map(() => "---").join(" | ")} |`;

  const dataRows = rows.map((row) => {
    const cells = columns.map((col) => {
      if (VARIATION_FIELDS.has(col)) {
        const val = row[col];
        return typeof val === "string" ||
          typeof val === "number" ||
          typeof val === "boolean"
          ? String(val)
          : JSON.stringify(val);
      }
      const isPct = isPercentMetric(col);
      return formatMetricValue(row[col] as MetricValue, isPct);
    });
    return `| ${cells.join(" | ")} |`;
  });

  return [headerRow, separatorRow, ...dataRows].join("\n");
}
